/**
 * Кто имеет доступ к аккаунту.
 *
 * Это организационный реестр, а не хранилище доступов: он отвечает на вопрос
 * «кто сможет войти, если человек уйдёт». Пароля, ключа и recovery-кода здесь
 * нет и быть не может — они живут во внешнем менеджере паролей, а в системе
 * остаётся только название записи.
 */

import { Loader2Icon, ShieldPlusIcon, UserMinusIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { UserDto } from '@project-os/contracts';
import { ACCOUNT_ACCESS_LEVELS, ACCOUNT_ACCESS_LEVEL_LABELS_RU } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useAccountAccess, useGrantAccess, useRevokeAccess } from '@/api/resources';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/common/user-avatar';
import { formatDate } from '@/lib/format';

export function AccountAccessSection({
  projectId,
  itemId,
  members,
  canEdit,
}: {
  projectId: string;
  itemId: string;
  members: UserDto[];
  canEdit: boolean;
}) {
  const { data: access, isPending } = useAccountAccess(projectId, itemId);
  const grant = useGrantAccess(projectId, itemId);
  const revoke = useRevokeAccess(projectId, itemId);

  const [userId, setUserId] = useState('');
  const [level, setLevel] = useState<string>('USER');
  const [notes, setNotes] = useState('');

  const granted = new Set((access ?? []).map((entry) => entry.user.id));
  const candidates = members.filter((member) => !granted.has(member.id));

  const add = async () => {
    try {
      await grant.mutateAsync({
        userId,
        accessLevel: level as (typeof ACCOUNT_ACCESS_LEVELS)[number],
        notes: notes.trim() || null,
      });
      setUserId('');
      setNotes('');
      toast.success('Доступ отмечен');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось записать доступ');
    }
  };

  const remove = async (accessId: string, name: string) => {
    try {
      await revoke.mutateAsync(accessId);
      toast.success(`Доступ ${name} отозван`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось отозвать доступ');
    }
  };

  if (isPending) return <Skeleton className="h-16 w-full" />;

  return (
    <div className="space-y-3">
      {access && access.length > 0 ? (
        <ul className="space-y-1.5">
          {access.map((entry) => (
            <li
              key={entry.id}
              className="surface-card flex items-center gap-2.5 border border-border/50 p-2.5"
            >
              <UserAvatar user={entry.user} size="md" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{entry.user.name}</p>
                <p className="mt-0.5 flex flex-wrap gap-x-2 text-2xs text-muted-foreground">
                  <span>{entry.labelRu}</span>
                  <span>с {formatDate(entry.grantedAt)}</span>
                  {entry.notes ? <span>{entry.notes}</span> : null}
                </p>
              </div>
              {canEdit ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0 text-muted-foreground hover:text-tone-danger"
                  disabled={revoke.isPending}
                  onClick={() => void remove(entry.id, entry.user.name)}
                  title="Отозвать доступ"
                >
                  <UserMinusIcon className="size-3.5" />
                  <span className="sr-only">Отозвать доступ {entry.user.name}</span>
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs leading-relaxed text-muted-foreground">
          Доступ ни за кем не закреплён. Если аккаунт нужен команде, отметьте, у кого он есть —
          иначе восстанавливать его будет некому.
        </p>
      )}

      {canEdit ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger size="sm" className="min-w-40 flex-1">
                <SelectValue placeholder="Кому" />
              </SelectTrigger>
              <SelectContent>
                {candidates.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    Все участники уже отмечены
                  </div>
                ) : (
                  candidates.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>

            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger size="sm" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCOUNT_ACCESS_LEVELS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {ACCOUNT_ACCESS_LEVEL_LABELS_RU[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Input
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Пометка: зачем выдан доступ. Не для паролей."
            className="h-8"
            aria-label="Примечание к доступу"
          />

          <Button size="sm" disabled={!userId || grant.isPending} onClick={() => void add()}>
            {grant.isPending ? <Loader2Icon className="animate-spin" /> : <ShieldPlusIcon />}
            Отметить доступ
          </Button>
        </div>
      ) : null}
    </div>
  );
}
